import {
  useClientInfoConfig,
  useUpdateClientInfoConfig,
  useUploadClientInfoConfigFile,
  useDeleteClientInfoConfigFile,
} from "@/api/platform/useClients";
import {
  useDepartmentInfoConfig,
  useUpdateInfoConfig,
  useUploadInfoConfigFile,
  useDeleteInfoConfigFile,
} from "@/api/platform/useDepartments";
import { ClientSelectSingle } from "@/components/form-v2/searchable-select/ClientSelect";
import { DepartmentSelectSingle } from "@/components/form-v2/searchable-select/DepartmentSelect";
import TextTemplate from "@/components/ui/Content/TextTemplate";
import { UserContext, useAuth } from "@/context/user";
import { Client, Department } from "@/types/userManagement";
import React, { useCallback, useContext, useRef, useState } from "react";

type DeleteTarget =
  | { scope: "client"; fileID: string; label: string }
  | { scope: "department"; fileID: string; label: string };

function extractError(err: unknown) {
  const anyErr = err as { response?: { data?: { message?: string }; status?: number }; message?: string };
  if (anyErr?.response?.data?.message) return String(anyErr.response.data.message);
  if (anyErr?.response?.status) return `HTTP ${anyErr.response.status}`;
  if (anyErr?.message) return String(anyErr.message);
  return "Unexpected error";
}

function formatBytes(bytes?: number) {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AppInformationUpload() {
  const auth = useAuth();
  const user = useContext(UserContext);

  const canManageClientInfo = auth.hasAccessLevelClient;
  const canManageDepartmentInfo = auth.hasAccessLevelDepartment;
  const isSuperAdmin = auth.isSuperAdmin;

  const [client, setClient] = useState<Client>();
  const [department, setDepartment] = useState<Department>();

  const [clientBusy, setClientBusy] = useState<string | null>(null);

  const [status, setStatus] = useState<{ kind: "success" | "error" | "info"; message: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const clientFileInputRef = useRef<HTMLInputElement>(null);
  const departmentFileInputRef = useRef<HTMLInputElement>(null);

  const clientID = client?.id ?? "";
  const departmentID = department?.id ?? "";

  const { data: clientInfoConfig, isLoading: loadingClientInfo } = useClientInfoConfig(clientID);
  const clientInformation = clientInfoConfig?.information ?? "";
  const clientFiles = clientInfoConfig?.files ?? [];

  const updateClientInfoConfig = useUpdateClientInfoConfig(clientID);
  const uploadClientInfoConfigFile = useUploadClientInfoConfigFile(clientID);
  const deleteClientInfoConfigFile = useDeleteClientInfoConfigFile(clientID);

  const { data: deptInfoConfig, isLoading: loadingDepartmentInfo } = useDepartmentInfoConfig(departmentID);
  const departmentInformation = deptInfoConfig?.information ?? "";
  const departmentFiles = deptInfoConfig?.files ?? [];

  const updateInfoConfig = useUpdateInfoConfig(departmentID);
  const uploadInfoConfigFile = useUploadInfoConfigFile(departmentID);
  const deleteInfoConfigFile = useDeleteInfoConfigFile(departmentID);

  const showStatus = useCallback((kind: "success" | "error" | "info", message: string) => {
    setStatus({ kind, message });
    if (kind !== "error") {
      window.setTimeout(() => setStatus(null), 3000);
    }
  }, []);

  const onClientChange = (selected?: Client) => {
    setClient(selected);
    setDepartment(undefined);
  };

  const saveClientInfo = async (nextInformation: string) => {
    if (!clientID) return;
    try {
      await updateClientInfoConfig.mutateAsync({ information: nextInformation });
      showStatus("success", "Client information updated");
    } catch (err) {
      showStatus("error", `Failed to update client info: ${extractError(err)}`);
    }
  };

  const saveDepartmentInfo = async (nextInformation: string) => {
    if (!departmentID) return;
    try {
      await updateInfoConfig.mutateAsync({ information: nextInformation });
      showStatus("success", "Department information updated");
    } catch (err) {
      showStatus("error", `Failed to update department info: ${extractError(err)}`);
    }
  };

  const uploadClientFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!clientID) return;
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setClientBusy("upload");
      await uploadClientInfoConfigFile.mutateAsync({ file, label: file.name });
      showStatus("success", "Client file uploaded");
    } catch (err) {
      showStatus("error", `Failed to upload client file: ${extractError(err)}`);
    } finally {
      setClientBusy(null);
      if (clientFileInputRef.current) clientFileInputRef.current.value = "";
    }
  };

  const uploadDepartmentFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!departmentID) return;
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      await uploadInfoConfigFile.mutateAsync({ file, label: file.name });
      showStatus("success", "Department file uploaded");
    } catch (err) {
      showStatus("error", `Failed to upload department file: ${extractError(err)}`);
    } finally {
      if (departmentFileInputRef.current) departmentFileInputRef.current.value = "";
    }
  };

  const downloadClientFile = async (fileID: string, label: string) => {
    if (!clientID) return;
    const url = `${process.env.REACT_APP_PLATFORM_API ?? ""}/api/clients/${clientID}/info-config/files/${fileID}`;
    try {
      setClientBusy(`download-${fileID}`);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectURL;
      a.download = label;
      a.click();
      URL.revokeObjectURL(objectURL);
    } catch (err) {
      showStatus("error", `Failed to download file: ${extractError(err)}`);
    } finally {
      setClientBusy(null);
    }
  };

  const downloadDepartmentFile = async (fileID: string, label: string) => {
    if (!departmentID) return;
    const url = `${process.env.REACT_APP_PLATFORM_API ?? ""}/api/departments/${departmentID}/info-config/files/${fileID}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectURL = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectURL;
      a.download = label;
      a.click();
      URL.revokeObjectURL(objectURL);
    } catch (err) {
      showStatus("error", `Failed to download file: ${extractError(err)}`);
    }
  };

  const deleteClientFile = async (fileID: string) => {
    if (!clientID) return;
    try {
      setClientBusy(`delete-${fileID}`);
      await deleteClientInfoConfigFile.mutateAsync(fileID);
      showStatus("success", "Client file deleted");
    } catch (err) {
      showStatus("error", `Failed to delete file: ${extractError(err)}`);
    } finally {
      setClientBusy(null);
    }
  };

  const deleteDepartmentFile = async (fileID: string) => {
    if (!departmentID) return;
    try {
      await deleteInfoConfigFile.mutateAsync(fileID);
      showStatus("success", "Department file deleted");
    } catch (err) {
      showStatus("error", `Failed to delete file: ${extractError(err)}`);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.scope === "client") {
      await deleteClientFile(deleteTarget.fileID);
    } else {
      await deleteDepartmentFile(deleteTarget.fileID);
    }
    setDeleteTarget(null);
  };

  const clientMutationBusy = updateClientInfoConfig.isLoading || uploadClientInfoConfigFile.isLoading || deleteClientInfoConfigFile.isLoading;
  const deptBusy = updateInfoConfig.isLoading || uploadInfoConfigFile.isLoading || deleteInfoConfigFile.isLoading;

  return (
    <div className="container mx-auto p-4 space-y-6 h-[calc(100vh-260px)] overflow-y-auto pr-2">
      {status && (
        <div
          className={`rounded px-4 py-3 text-white ${
            status.kind === "success" ? "bg-green" : status.kind === "error" ? "bg-red" : "bg-blue"
          }`}
        >
          {status.message}
        </div>
      )}

      {canManageClientInfo && (
        <section className="bg-white rounded-lg shadow-md p-5 space-y-4">
          <h2 className="text-lg font-semibold text-grey">Client App Information</h2>
          <div className="max-w-xl">
            <ClientSelectSingle
              client={client}
              onChange={onClientChange}
              initialClientID={auth.clientID || undefined}
              disabled={!isSuperAdmin}
            />
          </div>
          {client && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-grey">Information (HTML)</p>
                <p className="text-xs text-grey">Use editor Save to apply changes</p>
              </div>
              <div className={`${clientBusy !== null || clientMutationBusy || loadingClientInfo ? "pointer-events-none opacity-70" : ""} max-h-[340px] overflow-y-auto rounded-md`}>
                <TextTemplate
                  key={`client-info-${client.id}`}
                  initialContent={clientInformation}
                  onSave={(html) => void saveClientInfo(html)}
                  height="260px"
                />
              </div>
              <div className="flex gap-3">
                <input
                  ref={clientFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={uploadClientFile}
                  disabled={clientBusy !== null || clientMutationBusy || loadingClientInfo}
                />
                <button
                  className="bg-blue text-white rounded px-4 py-2 font-medium shadow-sm hover:brightness-95 disabled:opacity-60"
                  onClick={() => clientFileInputRef.current?.click()}
                  disabled={clientBusy !== null || clientMutationBusy || loadingClientInfo}
                >
                  {clientBusy === "upload" ? "Uploading..." : "Upload Client File"}
                </button>
              </div>
              <div className="space-y-2">
                {clientFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-[#F6F7F9] rounded p-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{file.label}</div>
                      <div className="text-xs text-grey">
                        {[file.mimeType || "", formatBytes(file.sizeBytes)].filter(Boolean).join(" • ")}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="bg-green text-white rounded px-3 py-1.5 text-sm disabled:opacity-60"
                        onClick={() => void downloadClientFile(file.id, file.label)}
                        disabled={clientBusy !== null}
                      >
                        Download
                      </button>
                      <button
                        className="bg-red text-white rounded px-3 py-1.5 text-sm disabled:opacity-60"
                        onClick={() => setDeleteTarget({ scope: "client", fileID: file.id, label: file.label })}
                        disabled={clientBusy !== null}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!loadingClientInfo && clientFiles.length === 0 && (
                  <p className="text-sm text-grey">No files uploaded for this client.</p>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {canManageDepartmentInfo && (
        <section className="bg-white rounded-lg shadow-md p-5 space-y-4">
          <h2 className="text-lg font-semibold text-grey">Department App Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ClientSelectSingle
              client={client}
              onChange={onClientChange}
              initialClientID={auth.clientID || undefined}
              disabled={!isSuperAdmin}
            />
            <DepartmentSelectSingle
              clientID={client?.id}
              department={department}
              onChange={setDepartment}
              initialDepartmentID={!auth.hasAccessLevelClient ? user?.metadata?.departmentID : undefined}
              disabled={!client?.id}
            />
          </div>
          {department && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-grey">Information (HTML)</p>
                <p className="text-xs text-grey">Use editor Save to apply changes</p>
              </div>
              <div className={`${deptBusy || loadingDepartmentInfo ? "pointer-events-none opacity-70" : ""} max-h-[340px] overflow-y-auto rounded-md`}>
                <TextTemplate
                  key={`department-info-${department.id}`}
                  initialContent={departmentInformation}
                  onSave={(html) => void saveDepartmentInfo(html)}
                  height="260px"
                />
              </div>
              <div className="flex gap-3">
                <input
                  ref={departmentFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={uploadDepartmentFile}
                  disabled={deptBusy || loadingDepartmentInfo}
                />
                <button
                  className="bg-blue text-white rounded px-4 py-2 font-medium shadow-sm hover:brightness-95 disabled:opacity-60"
                  onClick={() => departmentFileInputRef.current?.click()}
                  disabled={deptBusy || loadingDepartmentInfo}
                >
                  {uploadInfoConfigFile.isLoading ? "Uploading..." : "Upload Department File"}
                </button>
              </div>
              <div className="space-y-2">
                {departmentFiles.map((file) => (
                  <div key={file.id} className="flex items-center justify-between bg-[#F6F7F9] rounded p-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{file.label}</div>
                      <div className="text-xs text-grey">
                        {[file.mimeType || "", formatBytes(file.sizeBytes)].filter(Boolean).join(" • ")}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="bg-green text-white rounded px-3 py-1.5 text-sm disabled:opacity-60"
                        onClick={() => downloadDepartmentFile(file.id, file.label)}
                        disabled={deptBusy}
                      >
                        Download
                      </button>
                      <button
                        className="bg-red text-white rounded px-3 py-1.5 text-sm disabled:opacity-60"
                        onClick={() => setDeleteTarget({ scope: "department", fileID: file.id, label: file.label })}
                        disabled={deptBusy}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {!loadingDepartmentInfo && departmentFiles.length === 0 && (
                  <p className="text-sm text-grey">No files uploaded for this department.</p>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Delete file?</h3>
            <p className="text-sm text-grey mb-6">
              This will permanently delete <span className="font-semibold">{deleteTarget.label}</span>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded bg-light-grey text-grey"
                onClick={() => setDeleteTarget(null)}
                disabled={deptBusy || clientBusy !== null}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red text-white disabled:opacity-60"
                onClick={confirmDelete}
                disabled={deptBusy || clientBusy !== null}
              >
                {deptBusy || clientBusy !== null ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
